import React from 'react';
import { View, StyleSheet, ViewStyle, Text } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

interface ThemedContainerProps {
  children: React.ReactNode;
  style?: ViewStyle;
  variant?: 'background' | 'surface' | 'card';
  padding?: number;
  margin?: number;
  borderRadius?: number;
}

export const ThemedContainer: React.FC<ThemedContainerProps> = ({
  children,
  style,
  variant = 'surface',
  padding = 16,
  margin = 0,
  borderRadius = 8,
}) => {
  const { themeData } = useTheme();

  const getBackgroundColor = () => {
    switch (variant) {
      case 'background':
        return themeData.colors.background;
      case 'surface':
        return themeData.colors.surface;
      case 'card':
        return themeData.colors.surface;
      default:
        return themeData.colors.surface;
    }
  };

  const containerStyle: ViewStyle = {
    backgroundColor: getBackgroundColor(),
    padding,
    margin,
    borderRadius,
    borderWidth: variant === 'card' ? 1 : 0,
    borderColor: variant === 'card' ? themeData.colors.border : 'transparent',
    ...style,
  };

  return (
    <View style={containerStyle}>
      {children}
    </View>
  );
};

export const ThemedText: React.FC<{
  children: React.ReactNode;
  style?: any;
  variant?: 'primary' | 'secondary' | 'accent';
  size?: 'small' | 'medium' | 'large' | 'title';
}> = ({ children, style, variant = 'primary', size = 'medium' }) => {
  const { themeData } = useTheme();

  const getTextColor = () => {
    switch (variant) {
      case 'primary':
        return themeData.colors.text;
      case 'secondary':
        return themeData.colors.textSecondary;
      case 'accent':
        return themeData.colors.accent;
      default:
        return themeData.colors.text;
    }
  };

  const getFontSize = () => {
    switch (size) {
      case 'small':
        return 12;
      case 'medium':
        return 16;
      case 'large':
        return 18;
      case 'title':
        return 24;
      default:
        return 16;
    }
  };

  // Safely merge styles to avoid CSS property issues
  const baseStyle = {
    color: getTextColor(),
    fontSize: getFontSize(),
    fontWeight: size === 'title' ? 'bold' : 'normal',
  };

  // Only spread style if it's a valid object
  const textStyle = style && typeof style === 'object' && !Array.isArray(style) 
    ? { ...baseStyle, ...style }
    : baseStyle;

  return <Text style={textStyle}>{children}</Text>;
};
